import { type ReactNode, useMemo } from "react";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import { IconButton, MenuItem, styled } from "~/common/mui";
import { SortDirection, type SortOption } from "~/features/search/models/models";
import { TextField } from "~/common/mui/TextField/TextField";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useSession } from "~/app/providers/SessionProvider";

interface OwnProps {
  className?: string;

  labelFactory?: (code: string) => ReactNode;
  label?: string | false;
}

interface FromStateProps {
  options: SortOption[];
  selectedCode: string;
  selectedDirection: SortDirection | null;
}

interface DispatchProps {
  onSelect: (newCode: string) => void;
  onAscDesc: (newDirection: SortDirection) => void;
}

type SortingPickerProps = OwnProps &
  FromStateProps &
  DispatchProps & {
    useTranslationKeyForLabel?: boolean;
  };

const DEFAULT_LABEL_FACTORY: OwnProps["labelFactory"] = (code) => <Trans i18nKey={`sort.label.${code}`} />;

export const SortingPicker = (props: SortingPickerProps) => {
  const { selectedCode, selectedDirection, options, onAscDesc, onSelect, className, useTranslationKeyForLabel } = props;
  const { t } = useTranslation();
  const session = useSession();
  const labelFactory = props.labelFactory ?? DEFAULT_LABEL_FACTORY;
  const useTranslationKey = !!useTranslationKeyForLabel || !session?.commerceContext.zoovuEnabled;
  const dropdownOptions = useMemo(
    () =>
      options.map((item) => {
        return {
          value: item.code,
          label: item.code === "relevance" || useTranslationKey ? labelFactory(item.code) : item.name,
          direction: item.sortingDirection,
        };
      }),
    [options, labelFactory, useTranslationKey],
  );

  if (!options || options.length === 0) {
    return null;
  }

  const title = t("searchPage.sort.buttonLabel");
  let label;
  if (props.label === false) {
    label = undefined;
  } else {
    label = props.label ?? t("searchPage.sort.title");
  }

  const showSortingArrow = options.find((item) => item.code === selectedCode)?.showSortingArrow;

  const isAsc = selectedDirection === SortDirection.ASC;

  return (
    <SortPickerRoot className={classNames("scha__srp__sort-picker", className)}>
      <TextField
        select
        variant="outlined"
        label={label}
        onChange={(e) => onSelect(e.target.value as string)}
        value={selectedCode}
        fullWidth={true}
        size={"small"}
      >
        {dropdownOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      {showSortingArrow && (
        <IconButton
          variant="contained"
          size={"medium"}
          color={"secondary"}
          title={title}
          onClick={() => onAscDesc(isAsc ? SortDirection.DESC : SortDirection.ASC)}
        >
          {isAsc ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
        </IconButton>
      )}
    </SortPickerRoot>
  );
};

const SortPickerRoot = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));
